import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { adminAPI } from "../utils/api";
import { useToast } from "../utils/ToastContext";
import { useAuth } from "../utils/AuthContext";
import "./AdminUsersPage.css";

const EMPTY_FORM = {
  fullName: "",
  username: "",
  email: "",
  password: "",
  role: "assessor",
};

export default function AdminUsersPage() {
  const toast = useToast();
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [users, setUsers] = useState([]);
  const [roleChanges, setRoleChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const cancelDeleteRef = useRef(null);
  const confirmDeleteRef = useRef(null);
  const deleteDialogRef = useRef(null);
  const allowedRoles = isSuperAdmin
    ? ["reviewer", "assessor", "admin"]
    : ["reviewer", "assessor"];

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.listUsers();
      setUsers(data.data || []);
    } catch {
      toast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const { data } = await adminAPI.createUser(form);
      toast("User created successfully", "success");
      setUsers((current) => [data.data, ...current]);
      setForm(EMPTY_FORM);
      setShowPassword(false);
    } catch (err) {
      toast(
        err.response?.data?.message || err.message || "Failed to create user",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setDeleting(true);
    try {
      await adminAPI.deleteUser(selectedUser.id);
      setUsers((current) =>
        current.filter((user) => user.id !== selectedUser.id),
      );
      toast("User deleted successfully", "success");
      setSelectedUser(null);
    } catch (err) {
      toast(
        err.response?.data?.message || err.message || "Failed to delete user",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  };

  const saveRole = async (user) => {
    const role = roleChanges[user.id] || user.role;
    if (role === user.role) return;
    try {
      const { data } = await adminAPI.updateUserRole(user.id, role);
      setUsers((current) =>
        current.map((item) => (item.id === user.id ? data.data : item)),
      );
      setRoleChanges((current) => {
        const next = { ...current };
        delete next[user.id];
        return next;
      });
      toast("User role updated", "success");
    } catch (err) {
      toast(err.response?.data?.message || "Failed to update user role", "error");
    }
  };

  useEffect(() => {
    if (!selectedUser) return undefined;
    (deleting ? deleteDialogRef.current : cancelDeleteRef.current)?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) setSelectedUser(null);
      if (event.key === "Tab") {
        event.preventDefault();
        if (deleting) {
          deleteDialogRef.current?.focus();
        } else if (event.shiftKey) {
          confirmDeleteRef.current?.focus();
        } else {
          cancelDeleteRef.current?.focus();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedUser, deleting]);

  return (
    <div>
      <div className="section-heading">
        <div>
          <h1 className="section-title">User Management</h1>
          <p className="section-sub">
            {isSuperAdmin
              ? "Superadmin controls for administrators and team accounts"
              : "Create and manage assessor and reviewer accounts"}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 420px) 1fr",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div className="card">
          <div className="card-header">
            <span className="card-title">Create User</span>
          </div>
          <div className="card-body">
            <form
              onSubmit={handleSubmit}
              style={{ display: "grid", gap: "0.9rem" }}
            >
              <label className="form-group">
                <span className="form-label">Full Name</span>
                <input
                  className="form-input"
                  value={form.fullName}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      fullName: event.target.value,
                    }))
                  }
                  placeholder="Optional"
                />
              </label>

              <label className="form-group">
                <span className="form-label">Username</span>
                <input
                  className="form-input"
                  value={form.username}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      username: event.target.value,
                    }))
                  }
                  placeholder="Optional, auto-generated from email"
                />
              </label>

              <label className="form-group">
                <span className="form-label">Email *</span>
                <input
                  type="email"
                  className="form-input"
                  value={form.email}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  placeholder="name@organization.org"
                  required
                />
              </label>

              <label className="form-group">
                <span className="form-label">Password *</span>
                <span className="password-input-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-input password-input"
                    value={form.password}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        password: event.target.value,
                      }))
                    }
                    placeholder="Temporary password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-visibility-toggle"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </span>
              </label>

              <label className="form-group">
                <span className="form-label">Role</span>
                <select
                  className="form-select"
                  value={form.role}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      role: event.target.value,
                    }))
                  }
                >
                  {allowedRoles.map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              </label>

              <button className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner" /> : "Create User"}
              </button>
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Existing Users</span>
            <button
              className="btn btn-outline btn-sm"
              onClick={loadUsers}
              type="button"
            >
              Refresh
            </button>
          </div>
          <div className="card-body">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "2rem",
                }}
              >
                <div className="spinner" />
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state" style={{ margin: 0 }}>
                <div className="empty-state-title">No users found</div>
                <div className="empty-state-sub">
                  Create the first account using the form.
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {users.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.85rem 1rem",
                      border: "1px solid var(--border)",
                      borderRadius: "12px",
                      background: "var(--surface-2)",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: "var(--navy)" }}>
                        {user.fullName}
                      </div>
                      <div
                        style={{ fontSize: "0.82rem", color: "var(--text-2)" }}
                      >
                        {user.username} · {user.email}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "0.5rem",
                      }}
                    >
                      {user.role === "superadmin" ? (
                        <span className="user-role-pill superadmin">Superadmin</span>
                      ) : (
                        <>
                          <label
                            className="sr-only"
                            htmlFor={`role-${user.id}`}
                          >
                            Role for {user.fullName}
                          </label>
                          <select
                            id={`role-${user.id}`}
                            className="form-select user-role-select"
                            value={roleChanges[user.id] || user.role}
                            disabled={!isSuperAdmin && user.role === "admin"}
                            onChange={(event) =>
                              setRoleChanges((current) => ({
                                ...current,
                                [user.id]: event.target.value,
                              }))
                            }
                          >
                            {(user.role === "admin" && !isSuperAdmin
                              ? ["admin"]
                              : allowedRoles).map((role) => (
                                <option key={role} value={role}>
                                  {role.charAt(0).toUpperCase() + role.slice(1)}
                                </option>
                              ))}
                          </select>
                          {roleChanges[user.id] &&
                            roleChanges[user.id] !== user.role && (
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => saveRole(user)}
                              >
                                Save role
                              </button>
                            )}
                        </>
                      )}
                      <span
                        style={{ fontSize: "0.72rem", color: "var(--text-3)" }}
                      >
                        {user.active ? "Active" : "Inactive"}
                      </span>
                      {String(currentUser?.id || currentUser?._id) !== user.id &&
                        user.role !== "superadmin" &&
                        (isSuperAdmin || user.role !== "admin") && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => setSelectedUser(user)}
                          >
                            Delete account
                          </button>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedUser && (
        <div
          className="account-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deleting) {
              setSelectedUser(null);
            }
          }}
        >
          <section
            ref={deleteDialogRef}
            tabIndex={-1}
            className="account-delete-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-user-title"
            aria-describedby="delete-user-description"
          >
            <div className="account-delete-icon" aria-hidden="true">
              !
            </div>
            <p className="account-dialog-eyebrow">Account removal</p>
            <h2 id="delete-user-title">Delete this account?</h2>
            <p id="delete-user-description">
              This permanently removes <strong>{selectedUser.fullName}</strong>{" "}
              ({selectedUser.email}) and revokes their access immediately.
            </p>
            <div className="account-dialog-actions">
              <button
                ref={cancelDeleteRef}
                type="button"
                className="btn btn-outline"
                disabled={deleting}
                onClick={() => setSelectedUser(null)}
              >
                Keep account
              </button>
              <button
                ref={confirmDeleteRef}
                type="button"
                className="btn btn-danger"
                disabled={deleting}
                onClick={handleDelete}
              >
                {deleting ? (
                  <>
                    <span className="spinner" /> Deleting…
                  </>
                ) : (
                  "Delete permanently"
                )}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
